import React, {useEffect, useState} from 'react';
import {Input, Tooltip} from 'antd';
import {QuestionCircleOutlined} from '@ant-design/icons';
import EditableCard from 'components/EditableCard';
import { useTranslation } from 'react-i18next';

export interface DeliverableZoneFormData {
  zipcodes: string[];
}

interface DeliverableZoneFormProps {
  data: DeliverableZoneFormData;
  isEditing?: boolean;
  onEdit?: () => void;
  onSave?: (data: DeliverableZoneFormData) => void;
  onCancel?: () => void;
  title?: string;
  loading?: boolean;
  bordered?: boolean;
}

const DeliverableZoneForm : React.FC<DeliverableZoneFormProps> = ({
  data,
  isEditing = false,
  onEdit,
  onSave,
  onCancel,
  title,
  loading = false,
  bordered = false,
}) => {

  const [internalZipcodes, setInternalZipcodes] = useState<string>("");
  const { t } = useTranslation();
  useEffect(() => {
    setInternalZipcodes(data.zipcodes.join(', '));
  }, [data.zipcodes]);

  // 监听编辑状态变化，取消编辑时重置为原始数据
  useEffect(() => {
    if (!isEditing) {
      setInternalZipcodes(data.zipcodes.join(', '));
    }
  }, [isEditing, data.zipcodes]);

  // 解析和验证 zipcode 的函数
  const parseAndValidateZipcodes = (input: string) => {
    // 支持多种分隔符：逗号、空格、换行、分号、制表符
    const rawZipcodes = input
      .split(/[,\s\n\r\t;]+/)
      .map(code => code.trim())
      .filter(code => code.length > 0);

    // 验证每个 zipcode
    const validZipcodes: string[] = [];
    const invalidZipcodes: string[] = [];
    const duplicates: string[] = [];

    rawZipcodes.forEach(code => {
      // zipcode 格式验证：只允许数字
      const isValidFormat = /^\d+$/.test(code);

      if (!isValidFormat) {
        invalidZipcodes.push(code);
      } else if (validZipcodes.includes(code)) {
        if (!duplicates.includes(code)) {
          duplicates.push(code);
        }
      } else {
        validZipcodes.push(code);
      }
    });

    return {
      validZipcodes,
      invalidZipcodes,
      duplicates,
      totalCount: rawZipcodes.length
    };
  };

  const handleSave = () => {
    const { validZipcodes, invalidZipcodes, duplicates } = parseAndValidateZipcodes(internalZipcodes);

    // 如果有无效数据或重复数据，阻止保存
    if (invalidZipcodes.length > 0 || duplicates.length > 0) {
      return; // 不执行保存操作
    }

    onSave?.({ zipcodes: validZipcodes });
  };

  // 计算统计信息
  const {
    validZipcodes,
    invalidZipcodes,
    duplicates,
    totalCount
  } = parseAndValidateZipcodes(internalZipcodes);

  const titleWithTooltip = (
    <span>
      {title || t('central.sites.details.deliverableZone')}
      <Tooltip title={t('central.sites.details.deliverableZoneTooltip')}>
        <QuestionCircleOutlined className="ml-2 text-gray-400" />
      </Tooltip>
    </span>
  );

  return (
    <EditableCard
      title={titleWithTooltip}
      isEditing={isEditing}
      onEdit={onEdit}
      onSave={handleSave}
      onCancel={onCancel}
      loading={loading}
      bordered={bordered}
    >
      <Input.TextArea
        value={internalZipcodes}
        rows={4}
        readOnly={!isEditing}
        onChange={(e) => setInternalZipcodes(e.target.value)}
        placeholder={isEditing ? t('central.sites.details.enterZipcodesPlaceholder') as string : ""}
      />
      <div className="mt-2 space-y-1">
        <div className="text-gray-500">
          {t('central.sites.details.totalEntries')}: {totalCount} | {t('central.sites.details.valid')}: {validZipcodes.length}
        </div>

        {duplicates.length > 0 && (
          <div className="text-orange-500 text-sm">
            {duplicates.length} {duplicates.length > 1 ? t('central.sites.details.duplicateZipcodes') : t('central.sites.details.duplicateZipcode')}: {duplicates.join(', ')}
          </div>
        )}

        {invalidZipcodes.length > 0 && (
          <div className="text-red-500 text-sm">
            {invalidZipcodes.length} {invalidZipcodes.length > 1 ? t('central.sites.details.invalidZipcodes') : t('central.sites.details.invalidZipcode')}: {invalidZipcodes.slice(0, 3).join(', ')}{invalidZipcodes.length > 3 ? '...' : ''}
            <div className="text-xs mt-1">
              {t('central.sites.details.zipcodesMustBeNumbersOnly')}
            </div>
          </div>
        )}

        {(invalidZipcodes.length > 0 || duplicates.length > 0) && (
          <div className="text-red-500 text-sm font-medium">
             {t('central.sites.details.cannotSaveFixIssues')}
          </div>
        )}


      </div>
    </EditableCard>
  );
};

export default DeliverableZoneForm;

